import React, { useState, useEffect, FocusEvent, useRef, useMemo } from 'react';
import Select, {
  InputActionMeta,
  SingleValue,
  SelectInstance,
  components,
  InputProps,
  DropdownIndicatorProps,
  ActionMeta,
} from 'react-select';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { locationSelectors } from 'redux/modules/Location';
// eslint-disable-next-line import/no-unresolved
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import {
  patchCurrentLocation,
  getCitiesOfCountry,
} from 'redux/modules/Location/location';
import { ILocation, ICountry } from 'interfaces';
import {
  localizationTranslate,
  cityTranslate,
} from 'utils/localizationTranslate';
import {
  CustomDropdownIndicator,
  CityCustomOption,
  CustomDropdownIndicatorUpside,
  getCustomValueContainer,
} from '../SelectorComponents';
import { CitySelectStyles } from '../../LocationSelectors.styles';
import { CustomClearIndicator } from '../CountrySelector/CountrySelector';

const sortCities = (
  cityList: ILocation[] = [],
  inputValue: string,
  language: string,
  cityTranslateFn: (lang: string, city: ILocation) => string,
): ILocation[] => {
  const input = inputValue.toLowerCase().trim();
  const remote = cityList.find((city) => city.cityName === 'Remote');
  const remaining = cityList.filter((city) => city.cityName !== 'Remote');

  const startsWith = remaining
    .filter((city) =>
      cityTranslateFn(language, city).toLowerCase().startsWith(input),
    )
    .sort((a, b) =>
      cityTranslateFn(language, a).localeCompare(cityTranslateFn(language, b)),
    );

  const contains = remaining
    .filter(
      (city) =>
        !cityTranslateFn(language, city).toLowerCase().startsWith(input) &&
        cityTranslateFn(language, city).toLowerCase().includes(input),
    )
    .sort((a, b) =>
      cityTranslateFn(language, a).localeCompare(cityTranslateFn(language, b)),
    );

  const sorted = [...startsWith, ...contains];
  if (remote) sorted.push(remote);
  return sorted;
};

const CityDropdownInput = (props: InputProps<ILocation, false>) => (
  <components.Input {...props} data-testid='cityDropdownInput' />
);

const getCityDropdownIndicator = (isUpside: boolean) => {
  const CityDropdownIndicatorWithTestId = (
    props: DropdownIndicatorProps<ILocation, false>,
  ) => {
    if (props.selectProps.isClearable) {
      return null;
    }

    const Component = isUpside
      ? CustomDropdownIndicatorUpside
      : CustomDropdownIndicator;

    return <Component {...props} data-testid='cityDropdownToggle' />;
  };

  return CityDropdownIndicatorWithTestId;
};

type CountrySelectorProps = {
  country?: ICountry;
  handleCityChange: (selected: boolean) => void;
  disabled?: boolean;
  includeLocationIcon?: boolean;
};

export const CitySelector = ({
  country,
  handleCityChange,
  disabled,
  includeLocationIcon,
}: CountrySelectorProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const selectRef = useRef<SelectInstance<ILocation>>(null);
  const dispatch = useAppDispatch();
  const currentLocation = useAppSelector(locationSelectors.getCurrentLocation);
  const cities = useAppSelector(locationSelectors.getCitiesByCountry);

  const remoteCities = useMemo(() => {
    const byName = t('countriesByName', { returnObjects: true }) as Record<
      string,
      string
    >;

    if (country?.countryName !== 'Remote' || !Array.isArray(cities)) return [];

    return cities.map((city) => ({
      ...city,
      cityName:
        language === 'en'
          ? city.cityName
          : byName[city.cityName] ?? city.cityName,
    }));
  }, [t, language, country?.countryName, cities]);

  let translatedCountryValue = '';

  if (language === 'en') {
    translatedCountryValue = currentLocation?.cityName || '';
  } else if (currentLocation?.country?.countryName === 'Remote') {
    const byName = t('countriesByName', { returnObjects: true }) as Record<
      string,
      string
    >;
    translatedCountryValue =
      byName[currentLocation.cityName] || currentLocation.cityName;
  } else {
    translatedCountryValue = t(`countries.${currentLocation?.id}`);
  }

  const translatedCity =
    currentLocation?.country.countryName === 'Remote'
      ? translatedCountryValue
      : cityTranslate(language, currentLocation);

  const [inputValue, setInputValue] = useState<string>(translatedCity);
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);

  useEffect(() => {
    if (country?.countryName) {
      dispatch(getCitiesOfCountry(country.countryName));
    }
  }, [country, dispatch]);

  useEffect(() => {
    setInputValue(() =>
      currentLocation?.country.id === country?.id ? translatedCity : '',
    );
  }, [language, currentLocation, country, translatedCity]);

  const onClear = (actionMeta: ActionMeta<ILocation>) => {
    if (actionMeta.action === 'clear') {
      setInputValue('');
      setIsMenuOpened(true);
    }
  };

  const onOptionChange = async (
    option: SingleValue<ILocation>,
    actionMeta: ActionMeta<ILocation>,
  ) => {
    onClear(actionMeta);
    if (!option || option.id === currentLocation?.id) return;
    await dispatch(patchCurrentLocation(option));
    setInputValue(translatedCity);
    handleCityChange(true);
    selectRef.current?.blur();
  };

  const onInputChange = (newValue: string, { action }: InputActionMeta) => {
    if (action === 'input-change') setInputValue(newValue);
  };

  const onInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.select();
    }, 100);
  };

  const handleBlur = () => {
    const isSameCountry =
      currentLocation && currentLocation.country.id === country?.id;

    if (!inputValue && isSameCountry) {
      const restoredValue =
        currentLocation.cityName === 'Remote'
          ? localizationTranslate(language, currentLocation)
          : cityTranslate(language, currentLocation);
      setInputValue(restoredValue);
    }
  };

  function cityOrRemote(option: ILocation) {
    return option.cityName === 'Remote'
      ? localizationTranslate(language, option)
      : cityTranslate(language, option);
  }

  const options = sortCities(
    country?.countryName === 'Remote' ? remoteCities : cities,
    inputValue,
    language,
    cityTranslate,
  );

  const filterOption = (
    option: FilterOptionOption<ILocation>,
    input: string,
  ) => {
    if (option.data.cityName === 'Remote') return true;
    return localizationTranslate(language, option.data)
      .toLowerCase()
      .includes(input.toLowerCase());
  };

  const CustomValueContainer = useMemo(
    () => getCustomValueContainer(includeLocationIcon),
    [includeLocationIcon],
  );

  const DropdownIndicator = useMemo(
    () => getCityDropdownIndicator(isMenuOpened),
    [isMenuOpened],
  );

  const selectComponents = useMemo(
    () => ({
      Input: CityDropdownInput,
      DropdownIndicator,
      ValueContainer: CustomValueContainer,
      IndicatorSeparator: () => null,
      Option: CityCustomOption,
      ClearIndicator: CustomClearIndicator,
    }),
    [DropdownIndicator, CustomValueContainer],
  );

  return (
    <Select
      className='notranslate'
      ref={selectRef}
      value={currentLocation}
      inputValue={inputValue}
      isClearable={inputValue !== ''}
      options={options}
      isMulti={false}
      onChange={onOptionChange}
      onInputChange={onInputChange}
      placeholder={
        currentLocation?.country.id === country?.id && currentLocation
          ? cityOrRemote(currentLocation)
          : t('locationPage.enterCity')
      }
      noOptionsMessage={() => t('locationPage.notFoundCity')}
      components={selectComponents}
      styles={CitySelectStyles}
      getOptionLabel={(option) => cityOrRemote(option)}
      getOptionValue={(opt) => `${opt.id}`}
      isSearchable
      controlShouldRenderValue={false}
      backspaceRemovesValue={false}
      onFocus={onInputFocus}
      onBlur={handleBlur}
      onMenuOpen={() => setIsMenuOpened(true)}
      onMenuClose={() => setIsMenuOpened(false)}
      filterOption={filterOption}
      isDisabled={disabled}
      openMenuOnFocus
    />
  );
};
